import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
  SourceFilter,
  SourceFilterOp,
  SOURCE_FILTER_OP_LABELS,
  VALUELESS_FILTER_OPS,
} from '../../types/dataSource';

interface SourceFiltersEditorProps {
  filters: SourceFilter[];
  onChange: (filters: SourceFilter[]) => void;
  disabled?: boolean;
  /** Column names known for this source (e.g. from an ArcGIS inspect), used
   *  as suggestions; free text is always allowed. */
  columnOptions?: string[];
}

const OPS = Object.keys(SOURCE_FILTER_OP_LABELS) as SourceFilterOp[];

/** Row editor for a source's attribute filters (config.filters). Every row
 *  must match for a feature to be ingested. */
export default function SourceFiltersEditor({
  filters,
  onChange,
  disabled = false,
  columnOptions = [],
}: SourceFiltersEditorProps) {
  const handleAdd = () => onChange([...filters, { column: '', op: 'eq', value: '' }]);
  const handleRemove = (index: number) => onChange(filters.filter((_, i) => i !== index));
  const handleChange = (index: number, patch: Partial<SourceFilter>) =>
    onChange(filters.map((f, i) => (i === index ? { ...f, ...patch } : f)));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Feature Filters
        </Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={handleAdd} disabled={disabled}>
          Add filter
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Only features matching every filter are loaded. Text comparisons ignore case;
        “is one of” takes a comma-separated list. ArcGIS sources apply the filters on
        the server too, so only matching features are downloaded.
      </Typography>
      {columnOptions.length === 0 && filters.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Test the connection above to pick columns from the layer instead of typing them.
        </Typography>
      )}
      {filters.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
          No filters: every feature in the project area is loaded.
        </Typography>
      ) : (
        filters.map((filter, index) => {
          const valueless = VALUELESS_FILTER_OPS.includes(filter.op);
          return (
            <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
              <Autocomplete
                freeSolo
                size="small"
                options={columnOptions}
                value={filter.column}
                onInputChange={(_e, value) => handleChange(index, { column: value })}
                disabled={disabled}
                sx={{ flex: 1.2 }}
                renderInput={(params) => <TextField {...params} label="Column" />}
              />
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Operator</InputLabel>
                <Select
                  value={filter.op}
                  label="Operator"
                  onChange={(e) => handleChange(index, { op: e.target.value as SourceFilterOp })}
                  disabled={disabled}
                >
                  {OPS.map((op) => (
                    <MenuItem key={op} value={op}>
                      {SOURCE_FILTER_OP_LABELS[op]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Value"
                value={valueless ? '' : filter.value ?? ''}
                onChange={(e) => handleChange(index, { value: e.target.value })}
                disabled={disabled || valueless}
                placeholder={filter.op === 'in' || filter.op === 'not_in' ? 'a, b, c' : ''}
                sx={{ flex: 1.2 }}
              />
              <Tooltip title="Remove filter">
                <span>
                  <IconButton size="small" onClick={() => handleRemove(index)} disabled={disabled}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        })
      )}
    </Box>
  );
}
